import React, { useEffect } from 'react'
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper
} from '@mui/material';
import { COLORS } from '../../../Components/colors';
import { inferColumnDecimals, formatText, formatValue } from '../../../HelperFunctions/ReportHelpers';
import { calculateTotalRow } from '../../../Formulas/ReportFormulas';

const OTBReport = ({ reportData, reportPayload, pickupDaysLabel, displaySettings, selectedRows, totals, showLegalName }) => {

    const [columnDecimals, setColumnDecimals] = React.useState({});
    useEffect(() => {
        if (Array.isArray(reportData) && reportData.length > 0) {
            setColumnDecimals(inferColumnDecimals(reportData));
        }
    }, [reportData]);
    const revenueKeys = [
        "OTBADR", "OTBRoomRev", "OTBRevPar",
        "PickUpADR", "PickUpRev",
        "BudgetAdr", "BudgetRoomRev", "BudgetRevPar",
        "ADRVariance", "RoomRevenueVariance",
        "LYADR", "LYRoomRev", "LYRevPar",
        "LYADRVariance", "LYRoomRevenueVariance"
    ];
    const occupancyKeys = [
        "OTBOcc", "PickUpOcc", "BudgetOcc", "OccVariance", "LYOccupancy", "LYOccVariance"
    ];
    // const adrKeys = ["OTBADR", "PickUpADR", "BudgetAdr", "LYADR"];
    // const revParKeys = ["OTBRevPar", "BudgetRevPar", "LYRevPar"];
    // const roomRevKeys = ["OTBRoomRev", "PickUpRev", "BudgetRoomRev", "LYRoomRev"];
    // const roomsSoldKeys = ["OTBRoomsSold", "PickUpRooms", "BudgetRoomsSold", "LYRoomsSold"];
    

    const isMultiple = selectedRows?.length > 1 || (reportPayload?.CorporationIDLst && String(reportPayload.CorporationIDLst).includes(','));

    const reportStructure = [
        {
            title: "Details",
            columns: isMultiple ? [
                { label: "Corp Name", key: "CorporationName", dataKey: showLegalName ? "CorpLegalName" : "Corpname" },
                { label: "Legal Name", key: "LegalName", dataKey: "PCName" },
                { label: "Brand Name", key: "BrandName", dataKey: "BrandName" },
                { label: "Total Rooms", key: "TotalRooms", dataKey: "TotRooms" },
                { label: "Trns Rooms", key: "TrnsRooms", dataKey: "TransRooms" },
                { label: "Grp Rooms", key: "GrpRooms", dataKey: "GrpRooms" },
            ] : [
                { label: "Date", key: "Date", dataKey: "SaleDate" },
                { label: "DOW", key: "DOW", dataKey: "Dayweek" },
                { label: "Total Rooms", key: "TotalRooms", dataKey: "TotRooms" },
                { label: "Trns Rooms", key: "TrnsRooms", dataKey: "TransRooms" },
                { label: "Grp Rooms", key: "GrpRooms", dataKey: "GrpRooms" },
            ]
        },
        {
            title: "OTB Actuals",
            columns: [
                { label: "Curr Rooms Sold", key: "OTBRoomsSold", dataKey: "CurrRoomsSold" },
                { label: "Curr Occ", key: "OTBOcc", dataKey: "CurrOcc" },
                { label: "Curr ADR", key: "OTBADR", dataKey: "CurrADR" },
                { label: "Curr Rev Par", key: "OTBRevPar", dataKey: "CurrRevPar" },
                { label: "Curr Room Rev", key: "OTBRoomRev", dataKey: "CurrRoomRev" },
            ]
        },
        {
            title: pickupDaysLabel ? `${pickupDaysLabel} Pickup` : "Pickup",
            columns: [
                { label: "Rooms", key: "PickUpRooms", dataKey: "PickUpRooms" },
                { label: "Occ", key: "PickUpOcc", dataKey: "PickUpOcc" },
                { label: "ADR", key: "PickUpADR", dataKey: "PickUpADR" },
                { label: "Rev", key: "PickUpRev", dataKey: "PickUpRoomRev" },
            ]
        },
        {
            title: "Budget",
            columns: [
                { label: "Bud Sold", key: "BudgetRoomsSold", dataKey: "BudRoomsSold" },
                { label: "Bud Occ %", key: "BudgetOcc", dataKey: "BudOcc" },
                { label: "Bud ADR", key: "BudgetAdr", dataKey: "BudADR" },
                { label: "Bud RevPAR", key: "BudgetRevPar", dataKey: "BudRevPar" },
                { label: "Bud Rm Rev", key: "BudgetRoomRev", dataKey: "BudRoomRev" },
            ]
        },
        {
            title: "Day Variance",
            columns: [
                { label: "Rms(Bud)", key: "RoomsSoldVariance", dataKey: "RoomsSoldVar" },
                { label: "Occ %(Bud)", key: "OccVariance", dataKey: "OccVar" },
                { label: "ADR(Bud)", key: "ADRVariance", dataKey: "ADRVar" },
                { label: "Rev(Bud)", key: "RoomRevenueVariance", dataKey: "RoomRevVar" },
            ]
        },
        {
            title: "LY Actuals",
            columns: [
                { label: "LY Sold", key: "LYRoomsSold", dataKey: "LYRoomsSold" },
                { label: "LY Occ%", key: "LYOccupancy", dataKey: "LYOcc" },
                { label: "LY ADR", key: "LYADR", dataKey: "LYADR" },
                { label: "LY RevPAR", key: "LYRevPar", dataKey: "LYRevPar" },
                { label: "LY Rm Rev", key: "LYRoomRev", dataKey: "LYRoomRev" },
            ]
        },
        {
            title: "Day Variance",
            columns: [
                { label: "Rms(LY)", key: "LYRoomsSoldVariance", dataKey: "LYRoomsSoldVar" },
                { label: "Occ %(LY)", key: "LYOccVariance", dataKey: "LYOccVar" },
                { label: "ADR(LY)", key: "LYADRVariance", dataKey: "LYADRVar" },
                { label: "Rev(LY)", key: "LYRoomRevenueVariance", dataKey: "LYRoomRevVar" },
            ]
        }
    ];

    const isNumericColumn = (key) => {
        const nonNumeric = ['CorporationName', 'BrandName', 'LegalName', 'Date', 'DOW', 'PCName', 'CorpLegalName'];
        return !nonNumeric.includes(key);
    };

    // 1. Filter Rows (Exclude Zero Values)
    let processedData = Array.isArray(reportData) ? reportData : [];
    if (displaySettings?.excludeZero) {
        const numericKeysToCheck = [];
        reportStructure.forEach(section => {
            section.columns.forEach(col => {
                let isEnabled = false;
                if (isMultiple && (col.key === 'CorporationName' || col.key === 'LegalName' || col.key === 'BrandName')) isEnabled = true;
                else if (reportPayload && reportPayload[col.key]) isEnabled = true;

                if (isEnabled && isNumericColumn(col.key)) {
                    numericKeysToCheck.push(col.dataKey);
                }
            });
        });

        if (numericKeysToCheck.length > 0) {
            processedData = processedData.filter(row => {
                return numericKeysToCheck.some(key => {
                    const val = parseFloat(row[key]);
                    return !isNaN(val) && Math.abs(val) > 0.000001;
                });
            });
        }
    }

    const totalRow = React.useMemo(() => {
        const tRow = calculateTotalRow(processedData, totals);
        const pickupDataKeys = ["PickUpRooms", "PickUpOcc", "PickUpADR", "PickUpRoomRev"];
        if (processedData.length > 0 && tRow) {
            pickupDataKeys.forEach(key => {
                if (processedData.every(row => row[key] === 'N/A')) {
                    tRow[key] = 'N/A';
                }
            });
        }
        return tRow;
   }, [processedData, totals]);

    const visibleSections = reportStructure.map(section => ({
        ...section,
        visibleCols: section.columns.filter(col => {
            if (isMultiple && (col.key === 'CorporationName' || col.key === 'LegalName' || col.key === 'BrandName')) return true;
            if (!reportPayload || !reportPayload[col.key]) return false;
            return true;
        })
    })).filter(section => section.visibleCols.length > 0);

    const totalVisibleColumns = visibleSections.reduce((acc, section) => acc + section.visibleCols.length, 0);

    const commonCellStyle = {
        height: "32px",
        padding: "4px",
        border: `1px solid ${COLORS.border}`,
        boxSizing: "border-box",
        fontFamily: '"Poppins", sans-serif',
        color: COLORS.textPrimary,
        textAlign: "center",
        lineHeight: "normal",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        fontSize: "10.5px",
        maxWidth: "120px",
    };

    return (
        <TableContainer component={Paper} sx={{ width: "100%", height: "100%", overflow: "auto", whiteSpace: "nowrap", border: `1px solid ${COLORS.borderTableContainer}`, borderRadius: 0, boxShadow: 'none' }}>
            <Table size="small" stickyHeader sx={{ width: "100%", tableLayout: "auto" }}>

                <TableHead>
                    <TableRow>
                        {visibleSections.map((section, index) => (
                            <TableCell
                                key={index}
                                colSpan={section.visibleCols.length}
                                align="center"
                                sx={{
                                    backgroundColor: COLORS.bgHeader,
                                    border: `1px solid ${COLORS.border}`,
                                    whiteSpace: "nowrap",
                                    fontSize: 13,
                                    fontWeight: 600,
                                    fontFamily: '"Poppins", sans-serif',
                                    color: COLORS.textPrimary,
                                    p: 1,
                                    height: '35px',
                                    boxSizing: 'border-box',
                                    top: 0,
                                    zIndex: 2
                                }}
                            >
                                {formatText(section.title, displaySettings)}
                            </TableCell>
                        ))}
                    </TableRow>
                    <TableRow>
                        {visibleSections.map((section) =>
                            section.visibleCols.map((col) => {
                                let label = formatText(col.label, displaySettings);
                                if (occupancyKeys.includes(col.key) && !label.includes('%')) {
                                    label += " %";
                                }
                                return (
                                    <TableCell
                                        key={col.key}
                                        title={label}
                                        sx={{
                                            ...commonCellStyle,
                                            backgroundColor: COLORS.bgSubHeader,
                                            top: '35px',
                                            zIndex: 2
                                        }}
                                    >
                                        {label}
                                    </TableCell>
                                );
                            })
                        )}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {Array.isArray(processedData) && processedData.length > 0 ? (
                        processedData.map((row, rowIndex) => (
                            <TableRow key={rowIndex}>
                                {visibleSections.map((section) =>
                                    section.visibleCols.map((col) => {
                                        let cellValue = row[col.dataKey];
                                        if ((col.key === 'BrandName' || col.key === 'CorporationName') && !cellValue && selectedRows) {
                                            const matchedRow = selectedRows.find(r => r.corpName === row.Corpname);
                                            if (matchedRow) cellValue = col.key === 'BrandName' ? matchedRow.brand : (showLegalName ? matchedRow.legalName : matchedRow.corpName);
                                        }

                                        if (col.key === 'LegalName' && (!cellValue || cellValue === 'N/A')) {
                                            const matchedRow = selectedRows?.find(r => r.corpName === row.Corpname);
                                            cellValue = row.CorpLegalName || row.Corpname || matchedRow?.legalName || matchedRow?.corpName || '';
                                        }

                                        const isNum = isNumericColumn(col.key);
                                        let textColor = COLORS.textBlack;
                                        if (isNum && parseFloat(cellValue) < 0 && displaySettings?.showRedNegative) {
                                            textColor = COLORS.textRed;
                                        }

                                        let displayValue = formatValue(cellValue, col.dataKey, isNum, displaySettings, columnDecimals);
                                        if (!isNum) {
                                            displayValue = formatText(displayValue, displaySettings);
                                        } else if (displayValue !== '' && displayValue != null && displayValue !== 'N/A') {
                                            if (revenueKeys.includes(col.key)) {
                                                displayValue = `$${displayValue}`;
                                            } else if (occupancyKeys.includes(col.key)) {
                                                displayValue = `${displayValue}%`;
                                            }
                                        }

                                        return (
                                            <TableCell
                                                key={`${rowIndex}-${col.key}`}
                                                title={displayValue}
                                                sx={{
                                                    ...commonCellStyle,
                                                    color: textColor
                                                }}
                                            >
                                                {displayValue}
                                            </TableCell>
                                        );
                                    })
                                )}
                            </TableRow>
                        ))
                    ) : null}

                    {Array.isArray(processedData) && processedData.length > 0 && totalRow && (
                        <TableRow>
                            {visibleSections.map((section) =>
                                section.visibleCols.map((col) => {
                                    if (!isNumericColumn(col.key)) {
                                        const isTitleCol = isMultiple ? col.key === 'CorporationName' : col.key === 'Date';
                                        return (
                                            <TableCell
                                                key={`total-${col.key}`}
                                                sx={{ ...commonCellStyle, fontWeight: 'bold', backgroundColor: COLORS.bgLighter }}
                                            >
                                                {isTitleCol ? 'Total' : ''}
                                            </TableCell>
                                        );
                                    }

                                    const cellValue = totalRow[col.dataKey];
                                    let textColor = COLORS.textBlack;
                                    if (parseFloat(cellValue) < 0 && displaySettings?.showRedNegative) {
                                        textColor = COLORS.textRed;
                                    }

                                    let displayValue = formatValue(cellValue, col.dataKey, true, displaySettings, columnDecimals);
                                    if (displayValue !== '' && displayValue != null && displayValue !== 'N/A') {
                                        if (revenueKeys.includes(col.key)) {
                                            displayValue = `$${displayValue}`;
                                        } else if (occupancyKeys.includes(col.key)) {
                                            displayValue = `${displayValue}%`;
                                        }
                                    }

                                    return (
                                        <TableCell
                                            key={`total-${col.key}`}
                                            title={displayValue}
                                            sx={{
                                                ...commonCellStyle,
                                                color: textColor,
                                                fontWeight: 'bold',
                                                backgroundColor: COLORS.bgLighter
                                            }}
                                        >
                                            {displayValue}
                                        </TableCell>
                                    );
                                })
                            )}
                        </TableRow>
                    )}

                    {(!Array.isArray(processedData) || processedData.length === 0) && (
                        <TableRow>
                            <TableCell colSpan={totalVisibleColumns || 1} align="center" sx={{ color: COLORS.textLightGrey, py: 3 }}>
                                No data available
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

export default OTBReport